import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BaseLayout from './BaseLayout';
import FlameLoader from './FlameLoader';
import axios from 'axios';
import { API_BASE } from '../api';
import '../App.css';

const CATEGORIES = [
  { key: 'progression',  label: 'Progression',  icon: 'fa-bar-chart' },
  { key: 'first_time',   label: 'First Time',    icon: 'fa-star-o'   },
  { key: 'unobtainable', label: 'Unobtainable',  icon: 'fa-lock'     },
];

// ── Achievement key → reward item list ───────────────────────────────────
// Each item: { type, label, icon } where icon is a Font Awesome class
const ACHIEVEMENT_REWARDS = {
  // Particles
  on_fire_4:          [{ type: 'particles', label: 'Fire Particles',        icon: 'fa-fire'        }, { type: 'badge', label: 'Fire Lord Badge',        icon: 'fa-certificate' }],
  flame_picker_4:     [{ type: 'particles', label: 'Stars Particles',       icon: 'fa-star'        }, { type: 'badge', label: 'Legend Badge',           icon: 'fa-certificate' }],
  // Card effect
  login_novice:       [{ type: 'card',   label: 'Card Color',               icon: 'fa-th-large'    }],
  login_amateur:      [{ type: 'card',   label: 'Card Animations',          icon: 'fa-th-large'    }],
  wins_100:           [{ type: 'card',   label: 'Neon Card Effect',         icon: 'fa-th-large'    }, { type: 'name', label: 'Neon Name Effect', icon: 'fa-font' }, { type: 'border', label: 'Neon Border', icon: 'fa-circle-o' }, { type: 'banner', label: 'Neon Banner', icon: 'fa-picture-o' }],
  leaderboard_top1:   [{ type: 'card',   label: 'Gold Shimmer Card',        icon: 'fa-th-large'    }, { type: 'name', label: 'Gold Name Effect', icon: 'fa-font' }, { type: 'banner', label: 'Gold Banner', icon: 'fa-picture-o' }, { type: 'border', label: 'Gold Border', icon: 'fa-circle-o' }, { type: 'badge', label: 'GOAT Badge', icon: 'fa-certificate' }],
  // Name effect
  flame_picker_2:     [{ type: 'name',   label: 'Name Color',               icon: 'fa-font'        }],
  flame_picker_3:     [{ type: 'name',   label: 'Name Animations',          icon: 'fa-font'        }],
  leaderboard_top10:  [{ type: 'name',   label: 'Glimmer Name Effect',      icon: 'fa-font'        }, { type: 'badge', label: 'Elite Badge', icon: 'fa-certificate' }],
  // Tagline
  login_legend:       [{ type: 'tagline', label: 'Tagline',                 icon: 'fa-quote-left'  }, { type: 'badge', label: 'Legend Badge', icon: 'fa-certificate' }],
  // Banner
  login_professional: [{ type: 'banner', label: 'Custom Banner',            icon: 'fa-picture-o'   }],
  on_fire_3:          [{ type: 'banner', label: 'Heat Banner',              icon: 'fa-picture-o'   }, { type: 'badge', label: 'Flame Prince Badge', icon: 'fa-certificate' }],
  // Border
  wins_10:            [{ type: 'border', label: 'Border Color',             icon: 'fa-circle-o'    }],
  wins_50:            [{ type: 'border', label: 'Border Animations',        icon: 'fa-circle-o'    }],
  on_fire_2:          [{ type: 'border', label: 'Ember Border',             icon: 'fa-circle-o'    }],
  // Badges only
  social_2:           [{ type: 'badge',  label: 'Friendly Badge',           icon: 'fa-certificate' }],
  social_4:           [{ type: 'badge',  label: 'Popular Badge',            icon: 'fa-certificate' }],
  perfect_day:        [{ type: 'badge',  label: 'Perfectionist Badge',      icon: 'fa-certificate' }],
  loss_streak_10:     [{ type: 'badge',  label: '💩 Badge',                 icon: 'fa-certificate' }],
  all_leagues:        [{ type: 'badge',  label: 'All-Rounder Badge',        icon: 'fa-certificate' }],
  betrayal:           [{ type: 'badge',  label: 'Traitor Badge',            icon: 'fa-certificate' }],
  classic_mode:       [{ type: 'badge',  label: 'Nostalgic Badge',          icon: 'fa-certificate' }],
  cis4914:            [{ type: 'badge',  label: 'Founder Badge',            icon: 'fa-certificate' }],
};

// Generic type labels for the list-view summary (no specific theme names)
const REWARD_TYPE_LABELS = {
  particles: 'Particles', card: 'Card Effect', name: 'Name Effect',
  banner: 'Banner', border: 'Border', tagline: 'Tagline', badge: 'Badge',
};

// Build a compact reward summary: "+XP + Particles + Badge" etc.
function buildRewardSummary(ach) {
  const extras = ACHIEVEMENT_REWARDS[ach.key] || [];
  const parts = [];
  if (ach.reward_xp > 0) parts.push(`+${ach.reward_xp} XP`);
  const seen = new Set();
  for (const r of extras) {
    const lbl = REWARD_TYPE_LABELS[r.type] || r.type;
    if (!seen.has(lbl)) { seen.add(lbl); parts.push(lbl); }
  }
  return parts.length > 0 ? parts.join(' + ') : (ach.reward_description || null);
}

const AchievementCard = ({ achievement, onClick }) => {
  const { title, description, progress_total, user_progress, earned, earned_at,
          global_earned_pct, expires_at, category } = achievement;

  const pct = progress_total > 1
    ? Math.min(Math.round((user_progress / progress_total) * 100), 100)
    : (earned ? 100 : 0);

  const isUnobtainable  = category === 'unobtainable';
  const hasExpired      = expires_at && new Date(expires_at) < new Date();
  const formattedDate   = earned_at
    ? new Date(earned_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;
  const expiryDate      = expires_at
    ? new Date(expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  const rewardSummary = buildRewardSummary(achievement);

  return (
    <div
      className={`ach-card ${earned ? 'ach-card--earned' : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onClick()}
    >
      <div className={`ach-icon-wrap ${earned ? 'ach-icon-wrap--earned' : ''}`}>
        <i className={`fa ${earned ? 'fa-trophy' : 'fa-trophy'}`} />
      </div>

      <div className="ach-card-body">
        <div className="ach-card-header">
          <span className="ach-title">{title}</span>
          {earned && (
            <span className="ach-earned-pill">
              <i className="fa fa-check" /> Earned
            </span>
          )}
        </div>

        <p className="ach-desc">{description}</p>

        {/* Progress bar — not shown for unobtainable that have expired and weren't earned */}
        {!(isUnobtainable && hasExpired && !earned) && (
          <div className="ach-progress-wrap">
            <div className="ach-progress-track">
              <div className="ach-progress-fill" style={{ width: `${pct}%` }} />
            </div>
            {progress_total > 1 && (
              <span className="ach-progress-label">
                {Math.min(user_progress, progress_total).toLocaleString()} / {progress_total.toLocaleString()}
              </span>
            )}
          </div>
        )}

        {/* Unobtainable expired banner */}
        {isUnobtainable && hasExpired && !earned && (
          <p className="ach-expired-note">
            <i className="fa fa-lock" /> Unobtainable as of {expiryDate}
          </p>
        )}

        <div className="ach-card-footer">
          {rewardSummary && (
            <span className="ach-reward">{rewardSummary}</span>
          )}
          <span className="ach-global-pct">
            {global_earned_pct != null ? `${global_earned_pct.toFixed(1)}% of players earned this` : ''}
          </span>
          {earned && formattedDate && (
            <span className="ach-earned-date">Earned {formattedDate}</span>
          )}
        </div>
      </div>

      <div className="ach-card-arrow">
        <i className="fa fa-chevron-right" />
      </div>
    </div>
  );
};

const AchievementsPage = () => {
  const navigate = useNavigate();
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [activeTab, setActiveTab] = useState('progression');

  useEffect(() => {
    document.body.classList.add('achievements-page-bg');
    const fetch = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_BASE}/achievements/all`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAchievements(res.data.achievements || []);
      } catch {
        setError('Failed to load achievements. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetch();
    return () => document.body.classList.remove('achievements-page-bg');
  }, []);

  const byCategory = CATEGORIES.reduce((acc, c) => {
    acc[c.key] = achievements.filter(a => a.category === c.key);
    return acc;
  }, {});

  const earnedCount = achievements.filter(a => a.earned).length;

  return (
    <BaseLayout>
      <div className="ach-page">
        <div className="ach-page-header">
          <h1 className="ach-page-title">
            <i className="fa fa-trophy" style={{ color: 'var(--flame)', marginRight: 12 }} />
            Achievements
          </h1>
          {!loading && !error && (
            <span className="ach-summary-pill">
              {earnedCount} / {achievements.length} Earned
            </span>
          )}
        </div>

        {loading && <FlameLoader text="Loading achievements..." />}
        {error   && <p className="error-text">{error}</p>}

        {!loading && !error && (
          <>
            {/* Tabs */}
            <div className="ach-tabs">
              {CATEGORIES.map(c => {
                const cat  = byCategory[c.key] || [];
                const done = cat.filter(a => a.earned).length;
                return (
                  <button
                    key={c.key}
                    className={`ach-tab ${activeTab === c.key ? 'ach-tab--active' : ''}`}
                    onClick={() => setActiveTab(c.key)}
                  >
                    <i className={`fa ${c.icon}`} />
                    {c.label}
                    <span className="ach-tab-count">{done}/{cat.length}</span>
                  </button>
                );
              })}
            </div>

            {/* Achievement list */}
            <div className="ach-list">
              {(byCategory[activeTab] || []).length === 0 ? (
                <p className="ach-empty">No achievements in this category yet.</p>
              ) : (
                (byCategory[activeTab] || []).map(a => (
                  <AchievementCard
                    key={a.key}
                    achievement={a}
                    onClick={() => navigate(`/achievements/${a.key}`)}
                  />
                ))
              )}
            </div>
          </>
        )}
      </div>
    </BaseLayout>
  );
};

export default AchievementsPage;
